import { useMemo, useState, useEffect } from "react";
import DropdownSelector from "../../components/DropdownSelector";
import Label from "../../components/Label";
import Input from "../../components/Input";

const ALL_ELEMENTS = {
  korpusas: { id: "BODY", label: "korpusas" },
  "stalčius": { id: "DRAWER", label: "stalčius" },
};

// Required components that must all be mapped
const REQUIRED_COMPONENTS = [
  { id: "BODY", label: "korpusas", componentLabel: "cabinet.2.body" },
  { id: "DRAWER", label: "stalčius", componentLabel: "cabinet.2.drawer" },
];

// mock "detected" model components (dropdown options)
const MODEL_COMPONENTS = [
  { id: "korpusas", label: "cabinet.2.body" },
  { id: "stalcius", label: "cabinet.2.drawer" },
  { id: "kriaukle", label: "cabinet.2.sink" },
];

const EMPTY = { id: null, label: "— pasirinkti —" };

const ModelComponentMappingMock = ({
  colorValues = [],
  colorElementMappings = {},
  onReadyChange,
  initialMapping,
  initialSelectedColors,
  onStateChange,
}) => {
  // Derive which elements are available from color mappings
  const availableParts = useMemo(() => {
    const elementIds = new Set();
    colorValues.forEach((cv) => {
      const elements = colorElementMappings[cv];
      if (Array.isArray(elements)) {
        elements.forEach((id) => elementIds.add(id));
      }
    });
    return Array.from(elementIds)
      .map((id) => ALL_ELEMENTS[id])
      .filter(Boolean);
  }, [colorValues, colorElementMappings]);

  const [mapping, setMapping] = useState(initialMapping || {});
  const [selectedColors, setSelectedColors] = useState(() => {
    if (initialSelectedColors) return initialSelectedColors;
    // Default: all colors selected
    const all = {};
    colorValues.forEach((cv) => { all[cv] = true; });
    return all;
  });

  // All required components must be mapped for completion
  const isComplete = useMemo(
    () => REQUIRED_COMPONENTS.every((p) => mapping[p.id]?.id != null),
    [mapping],
  );

  const hasColors = colorValues.length > 0;
  const isReady = isComplete && hasColors;

  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);

  // Report state changes for caching
  useEffect(() => {
    onStateChange?.({ mapping, selectedColors });
    // eslint-disable-next-line
  }, [JSON.stringify(mapping), JSON.stringify(selectedColors)]);

  if (!hasColors) {
    return (
      <div
        style={{
          marginTop: "var(--gap)",
          padding: "var(--gap-sm)",
          borderRadius: "12px",
          border: "1px solid var(--clr-grey-300)",
        }}
      >
        <b style={{ color: "var(--clr-red-700)" }}>
          Nėra spalvų su elementų susiejimu. Pridėkite spalvų ir susiekite
          elementus per spalvų parametro redagavimą (pieštuko mygtukas).
        </b>
      </div>
    );
  }

  const unmappedComponents = REQUIRED_COMPONENTS.filter((p) => mapping[p.id]?.id == null);
  const unmappedComponentLabels = unmappedComponents.map((p) => p.componentLabel);

  return (
    <div
      style={{
        marginTop: "var(--gap)",
        padding: "var(--gap-sm)",
        borderRadius: "12px",
        border: "1px solid var(--clr-grey-300)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--gap-sm)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--gap-sm)",
          }}
        >
          <div style={{ width: "var(--width-32)" }}>Pavadinimas</div>
          <div style={{ width: "var(--width-64)" }}>Komponentas modelyje</div>
        </div>

        {availableParts.map((part) => {
          const selected = mapping[part.id] || EMPTY;

          return (
            <div
              key={part.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--gap-sm)",
              }}
            >
              <div style={{ width: "var(--width-32)" }}>
                <Label>{part.label}</Label>
              </div>
              <div style={{ width: "var(--width-64)" }}>
                <DropdownSelector
                  options={MODEL_COMPONENTS}
                  value={selected?.id ? selected : null}
                  defaultValue={EMPTY}
                  onChange={(option) => {
                    if (option?.id == null) {
                      setMapping((prev) => ({ ...prev, [part.id]: EMPTY }));
                      return;
                    }
                    setMapping((prev) => ({ ...prev, [part.id]: option }));
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ height: "var(--width-6)" }} />

      {!isComplete && (
        <div style={{ opacity: 0.85 }}>
          <b style={{ color: "var(--clr-red-700)" }}>
            {`Reikia susieti komponentus: ${unmappedComponentLabels.join(", ")}`}
          </b>
        </div>
      )}

      {isComplete && (
        <>
          <h3>Priskirti spalvų kombinacijas:</h3>
          {colorValues.map((colorValue, idx) => (
            <div key={colorValue} style={{ marginTop: "var(--width-2)" }}>
              <Input
                type="checkbox"
                name={`color_${idx}`}
                label={
                  typeof colorValue === "string"
                    ? colorValue
                    : String(colorValue)
                }
                value={selectedColors[colorValue] !== false}
                onChange={() =>
                  setSelectedColors((prev) => ({
                    ...prev,
                    [colorValue]: !prev[colorValue],
                  }))
                }
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default ModelComponentMappingMock;
